import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { supabase, supabaseEnabled, signInWithGoogle } from '../lib/supabase';
import { listCharacters, listScenes } from '../lib/savers';
import type { Attachment } from '../lib/db';

interface SocialFeedModalProps {
  onClose(): void;
  onInsert(prompt: string, refs: Attachment[]): void;
}

interface SharedItem {
  id: string;
  user_id: string;
  user_email: string;
  title: string;
  prompt: string;
  kind: 'character' | 'scene' | 'video' | 'image' | '3d';
  created_at: string;
  likes?: number;
}

export default function SocialFeedModal({ onClose, onInsert }: SocialFeedModalProps) {
  const [activeTab, setActiveTab] = useState<'feed' | 'share' | 'collaborators'>('feed');
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sharedItems, setSharedItems] = useState<SharedItem[]>([]);
  
  // Sharing form
  const [shareTitle, setShareTitle] = useState('');
  const [sharePrompt, setSharePrompt] = useState('');
  const [shareKind, setShareKind] = useState<'character' | 'scene' | 'video' | 'image' | '3d'>('character');
  const [sharing, setSharing] = useState(false);
  const [notification, setNotification] = useState('');

  // Collaborators
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const [collabEmail, setCollabEmail] = useState('');
  
  // Pre-load saved templates lists so they can quickly select what to share
  const [localChars, setLocalChars] = useState<any[]>([]);
  const [localScenes, setLocalScenes] = useState<any[]>([]);

  useEffect(() => {
    async function initUser() {
      if (supabaseEnabled && supabase) {
        const { data } = await supabase.auth.getUser();
        setUser(data?.user || null);
      }
      setLoading(false);
    }
    initUser();
  }, []);

  // Sync / fetch shared posts
  useEffect(() => {
    async function loadSocialFeed() {
      // High-grade fallback items so community is active out-of-the-box
      const demoItems: SharedItem[] = [
        {
          id: '1',
          user_id: 'demo-1',
          user_email: 'blade_runner_2099@protolive.ai',
          title: 'Neon Chiaroscuro Rain',
          prompt: '[Style: Cyberpunk Futurism], [Lighting: Neon Cyberpunk], wet glass reflections, dark atmospheric rainstorm, 4k detail',
          kind: 'scene',
          created_at: new Date(Date.now() - 3600000).toISOString()
        },
        {
          id: '2',
          user_id: 'demo-2',
          user_email: 'anime_director_charlie@gmail.com',
          title: '90s Cell Animation Face',
          prompt: '[Style: Retro Anime], extreme zoom close up of cybernetic pilot looking into bright engine lighting, cinematic haze',
          kind: 'character',
          created_at: new Date(Date.now() - 7200000).toISOString()
        },
        {
          id: '3',
          user_id: 'demo-3',
          user_email: 'gordon_lens@cinema.net',
          title: 'Supersonic Sound Wave Splash',
          prompt: '[FX: Shockwave Ripple], massive volumetric god rays exploding over desert horizon, cinematic 8k, slow-mo 1000fps',
          kind: '3d',
          created_at: new Date(Date.now() - 14400000).toISOString()
        }
      ];

      if (!supabaseEnabled || !supabase) {
        setSharedItems(demoItems);
        return;
      }

      try {
        // Attempt database query. If the relation doesn't exist yet, we catch the error gracefully and continue with fallbacks
        const { data, error } = await supabase
          .from('shared_items')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          console.warn('shared_items table query failed, using high-fidelity local feed:', error.message);
          setSharedItems(demoItems);
        } else if (data && data.length > 0) {
          setSharedItems(data as SharedItem[]);
        } else {
          setSharedItems(demoItems);
        }
      } catch (err) {
        console.warn('DB lookup error, defaulting to local community assets:', err);
        setSharedItems(demoItems);
      }
    }
    loadSocialFeed();

    // Load local items
    listCharacters().then(setLocalChars).catch(() => {});
    listScenes().then(setLocalScenes).catch(() => {});

    // Load collaborators from localstorage
    const saved = localStorage.getItem('protolive_collab');
    if (saved) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCollaborators(JSON.parse(saved));
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCollaborators(['alex_3d_artist@protolive.ai', 'cinemax_editor@paramount.com']);
    }
  }, [user]);

  function triggerNotify(msg: string) {
    setNotification(msg);
    setTimeout(() => setNotification(''), 2500);
  }

  async function handleShareSubmit() {
    if (!shareTitle.trim() || !sharePrompt.trim()) {
      triggerNotify('Please provide a title and template text.');
      return;
    }
    if (!user) {
      triggerNotify('Please login to share items.');
      return;
    }

    setSharing(true);
    const newPost: SharedItem = {
      id: crypto.randomUUID?.() || String(Math.random()),
      user_id: user.id,
      user_email: user.email || 'anonymous',
      title: shareTitle.trim(),
      prompt: sharePrompt.trim(),
      kind: shareKind,
      created_at: new Date().toISOString()
    };

    try {
      if (supabaseEnabled && supabase) {
        const { error } = await supabase.from('shared_items').insert([newPost]);
        if (error) throw error;
      }
      
      // Update local feed list state
      setSharedItems(prev => [newPost, ...prev]);
      setShareTitle('');
      setSharePrompt('');
      triggerNotify('🚀 Listed on the Public Feed!');
      setActiveTab('feed');
    } catch (err: any) {
      console.warn('Supabase publish tables not initiated. Simulated share local state successfully.', err);
      setSharedItems(prev => [newPost, ...prev]);
      setShareTitle('');
      setSharePrompt('');
      triggerNotify('🚀 Post successfully simulated!');
      setActiveTab('feed');
    } finally {
      setSharing(false);
    }
  }

  function handleAddCollaborator() {
    if (!collabEmail.trim() || !collabEmail.includes('@')) {
      triggerNotify('Please enter a valid email address.');
      return;
    }
    const updated = [...collaborators, collabEmail.trim()];
    setCollaborators(updated);
    localStorage.setItem('protolive_collab', JSON.stringify(updated));
    setCollabEmail('');
    triggerNotify('Collaborator Added!');
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="modal-backdrop"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 15, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ type: "spring", damping: 30, stiffness: 350 }}
        className="modal glass glass--strong"
        style={{ maxWidth: '480px', width: '100%', borderRadius: 'var(--radius-lg)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px' }}>🌐</span>
            <h2 className="sc" style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>Social Community</h2>
          </div>
          <button className="btn btn--ghost" style={{ minHeight: '26px', padding: '0 12px' }} onClick={onClose}>
            close
          </button>
        </div>

        {/* Global Notifications */}
        <AnimatePresence>
          {notification && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              style={{
                background: 'var(--glass-strong)',
                border: '1px solid var(--line-strong)',
                color: 'var(--fg)',
                fontSize: '11px',
                padding: '8px 12px',
                borderRadius: 'var(--radius-sm)',
                marginBottom: '10px',
                textAlign: 'center'
              }}
            >
              {notification}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Auth Check Prompt Block */}
        {!user && !loading ? (
          <div className="col" style={{ gap: '14px', alignItems: 'center', padding: '16px 0', textAlign: 'center' }}>
            <span style={{ fontSize: '30px' }}>🔐</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span className="sc" style={{ fontWeight: 'bold', fontSize: '13px' }}>Sign In Required</span>
              <p style={{ opacity: 0.7, fontSize: '11px', maxHeight: '300px', lineHeight: '1.4' }}>
                Join the Protolive social community! Sign up or Sign In to discover expert prompts, share models/environments, and collaborate with other users.
              </p>
            </div>
            <button className="btn btn--primary" onClick={() => signInWithGoogle()} style={{ minHeight: '36px', marginTop: '6px' }}>
              ⚡ Sign In with Google
            </button>
          </div>
        ) : (
          <div className="col" style={{ gap: '12px' }}>
            {/* Tab selection */}
            <div className="row" style={{ background: 'var(--line)', padding: '4px', borderRadius: 'var(--radius-sm)' }}>
              <button 
                className={`chip ${activeTab === 'feed' ? 'is-active' : ''}`} 
                style={{ flex: 1, fontSize: '9px', minHeight: '24px', justifyContent: 'center' }}
                onClick={() => setActiveTab('feed')}
              >
                Community Feed
              </button>
              <button 
                className={`chip ${activeTab === 'share' ? 'is-active' : ''}`} 
                style={{ flex: 1, fontSize: '9px', minHeight: '24px', justifyContent: 'center' }}
                onClick={() => setActiveTab('share')}
              >
                Share Prompt / Asset
              </button>
              <button 
                className={`chip ${activeTab === 'collaborators' ? 'is-active' : ''}`} 
                style={{ flex: 1, fontSize: '9px', minHeight: '24px', justifyContent: 'center' }}
                onClick={() => setActiveTab('collaborators')}
              >
                Collaborators
              </button>
            </div>

            {/* Content Tabs */}
            {activeTab === 'feed' && (
              <div className="col" style={{ gap: '10px', maxHeight: '310px', overflowY: 'auto', paddingRight: '4px' }}>
                {sharedItems.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', opacity: 0.5, fontSize: '11px' }}>
                    No shared posts yet. Be the first to publish a creation!
                  </div>
                ) : (
                  sharedItems.map(item => (
                    <div 
                      key={item.id} 
                      className="glass glass--flat" 
                      style={{ 
                        padding: '12px', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '8px', 
                        border: '1px solid var(--line-strong)',
                        background: 'rgba(255,255,255,0.02)'
                      }}
                    >
                      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: '10.5px', fontWeight: 'bold' }}>{item.title}</span>
                          <span style={{ fontSize: '8px', opacity: 0.5 }}>By {item.user_email}</span>
                        </div>
                        <span 
                          className="chip" 
                          style={{ 
                            fontSize: '7.5px', 
                            padding: '1px 5px', 
                            minHeight: '16px', 
                            borderRadius: '3px',
                            background: 'var(--glass)',
                            color: 'var(--fg)',
                            borderColor: 'var(--line)'
                          }}
                        >
                          {item.kind.toUpperCase()}
                        </span>
                      </div>

                      <div style={{ fontSize: '10px', color: 'var(--fg-dim)', whiteSpace: 'pre-wrap', lineHeight: '1.3' }}>
                        {item.prompt}
                      </div>

                      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--line)', paddingTop: '6px', fontSize: '9px' }}>
                        <span style={{ opacity: 0.5 }}>{new Date(item.created_at).toLocaleDateString()}</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button 
                            className="btn btn--primary" 
                            style={{ minHeight: '20px', fontSize: '8.5px', padding: '0 8px', borderRadius: '3px' }}
                            onClick={() => {
                              onInsert(item.prompt, []);
                              triggerNotify('Injecting template into active composer...');
                              onClose();
                            }}
                          >
                            Use Prompt →
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'share' && (
              <div className="col" style={{ gap: '10px' }}>
                <span className="sc" style={{ fontSize: '10.5px', color: 'var(--fg-dim)', fontWeight: 'bold' }}>Share with the Community</span>
                
                <div className="field">
                  <label>Title</label>
                  <input 
                    value={shareTitle} 
                    onChange={e => setShareTitle(e.target.value)} 
                    placeholder="e.g. Cinematic Chiaroscuro Face" 
                    style={{ minHeight: '32px', fontSize: '11px', padding: '8px' }}
                  />
                </div>

                <div className="field">
                  <label>Creation Type</label>
                  <select 
                    value={shareKind} 
                    onChange={e => setShareKind(e.target.value as any)}
                    style={{ minHeight: '32px', fontSize: '11px', padding: '4px' }}
                  >
                    <option value="character">Character Template</option>
                    <option value="scene">Scene Settings Template</option>
                    <option value="video">Cinematic Film Shot</option>
                    <option value="image">2D Generated Render</option>
                    <option value="3d">3D Skeletal Mesh</option>
                  </select>
                </div>

                {/* Preload helpers if they have local saved templates */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '8px', color: 'var(--fg-dim)' }}>Quick Load Local templates:</span>
                  <div className="row row--wrap" style={{ gap: '4px' }}>
                    {localChars.map(c => (
                      <button 
                        key={c.id} 
                        type="button" 
                        style={{ fontSize: '7.5px', padding: '2px 6px', minHeight: '18px' }}
                        className="chip"
                        onClick={() => {
                          setShareTitle(c.name);
                          setSharePrompt(c.prompt);
                        }}
                      >
                        👤 {c.name}
                      </button>
                    ))}
                    {localScenes.map(s => (
                      <button 
                        key={s.id} 
                        type="button" 
                        style={{ fontSize: '7.5px', padding: '2px 6px', minHeight: '18px' }}
                        className="chip"
                        onClick={() => {
                          setShareTitle(s.name);
                          setSharePrompt(s.prompt);
                        }}
                      >
                        🎬 {s.name}
                      </button>
                    ))}
                    {localChars.length === 0 && localScenes.length === 0 && (
                      <span style={{ fontSize: '7.5px', opacity: 0.5 }}>No local creations saved yet. Feel free to type manually below.</span>
                    )}
                  </div>
                </div>

                <div className="field">
                  <label>Prompt Terms & Descriptions</label>
                  <textarea 
                    rows={3} 
                    value={sharePrompt} 
                    onChange={e => setSharePrompt(e.target.value)} 
                    placeholder="Enter prompt description terms or tags..." 
                    style={{ fontSize: '11px', padding: '8px', resize: 'none' }}
                  />
                </div>

                <button 
                  className="btn btn--primary" 
                  style={{ minHeight: '32px', fontSize: '11px', marginTop: '6px' }}
                  disabled={sharing}
                  onClick={handleShareSubmit}
                >
                  {sharing ? 'Publishing...' : 'List in Public Feed'}
                </button>
              </div>
            )}

            {activeTab === 'collaborators' && (
              <div className="col" style={{ gap: '10px' }}>
                <span className="sc" style={{ fontSize: '10.5px', color: 'var(--fg-dim)', fontWeight: 'bold' }}>Add Collaborators / Share Access</span>
                
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input 
                    type="email" 
                    value={collabEmail} 
                    onChange={e => setCollabEmail(e.target.value)} 
                    placeholder="Enter partner email (e.g. director@cinema.com)" 
                    style={{ 
                      flex: 1, 
                      background: 'rgba(0,0,0,0.2)', 
                      border: '1px solid var(--line)', 
                      borderRadius: 'var(--radius-sm)', 
                      padding: '6px 8px', 
                      fontSize: '11px' 
                    }}
                  />
                  <button 
                    className="btn btn--primary" 
                    style={{ minHeight: '28px', fontSize: '10px', padding: '0 12px' }}
                    onClick={handleAddCollaborator}
                  >
                    Add
                  </button>
                </div>

                <span style={{ fontSize: '9px', color: 'var(--fg-dim)', fontWeight: 'bold', marginTop: '6px' }}>List of Collaborators:</span>
                <div className="col" style={{ maxHeight: '150px', overflowY: 'auto', gap: '4px' }}>
                  {collaborators.map(email => (
                    <div 
                      key={email} 
                      className="row" 
                      style={{ 
                        justifyContent: 'space-between', 
                        padding: '6px 10px', 
                        background: 'rgba(255,255,255,0.01)', 
                        border: '1px solid var(--line)', 
                        borderRadius: 'var(--radius-sm)' 
                      }}
                    >
                      <span style={{ fontSize: '10px' }}>{email}</span>
                      <button 
                        style={{ background: 'transparent', border: 'none', color: 'var(--fg)', cursor: 'pointer', fontSize: '9px', textDecoration: 'underline' }}
                        onClick={() => {
                          const updated = collaborators.filter(v => v !== email);
                          setCollaborators(updated);
                          localStorage.setItem('protolive_collab', JSON.stringify(updated));
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
